import WithLoader from "~/ui-rtk/components/layout/WithLoader/WithLoader"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { Breadcrumbs } from "~/ui-rtk/components/features/dashboards/BrandMediaOptimizationDashboard/components/Breadcrumbs/index"
import useConnect, {
  CREATIVE_REPORT_OPTIONS,
  CREATIVE_REPORTS_METRICS_MAP,
  DEFAULT_METRICS,
  ALWAYS_METRICS,
  type TCreativeReport,
  type TMetricsMapKey,
  ITEMS_PER_PAGE,
  MONETARY_METRICS,
  COEF_METRICS,
  PERCENTAGE_METRICS,
  BRAND_METRICS,
  REVENUE_METRICS,
  ENGAGEMENT_METRICS,
  PERFORMANCE_METRICS,
} from "./connect"
import { useEffect, useMemo, useState } from "react"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import {
  ChevronDownIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { DateRangePicker } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { CaptionPreview, Loader } from "~/ui-rtk/components/ui/common"
import useUrlPagination from "~/ui-rtk/hooks/url-pagination"
import CreativeReportCard from "./components/CreativeReportCard"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlSorting, { TSortingState } from "~/ui-rtk/hooks/url-sorting"
import SortByControl from "~/ui-rtk/components/ui/controls/SortBy/SortBy"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
  TMultiSelectWithChipsOptionGroup,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"

const MODE_URL_KEY = "report-mode"
const METRICS_URL_KEY = "metrics"

// Define metric groups
const METRIC_GROUPS: TMultiSelectWithChipsOptionGroup[] = [
  { label: "Brand Metrics", value: "brand" },
  { label: "Revenue Metrics", value: "revenue" },
  { label: "Platform Engagements", value: "engagements" },
  { label: "Performance Metrics", value: "performance" },
]

// Helper function to get group for a metric
const getMetricGroup = (metric: TMetricsMapKey): string => {
  if (BRAND_METRICS.includes(metric)) return "brand"
  if (REVENUE_METRICS.includes(metric)) return "revenue"
  if (ENGAGEMENT_METRICS.includes(metric)) return "engagements"
  if (PERFORMANCE_METRICS.includes(metric)) return "performance"
  return "performance" // default group
}

const CREATIVE_REPORTS_METRICS_OPTIONS = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).map(
  (key: string) =>
    ({
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    }) as TMultiSelectWithChipsOption,
)

const CREATIVE_REPORTS_METRICS_OPTIONS_MAP = Object.keys(
  CREATIVE_REPORTS_METRICS_MAP,
).reduce(
  (acc: Record<TMetricsMapKey, TMultiSelectWithChipsOption>, key: string) => ({
    ...acc,
    [key]: {
      label: CREATIVE_REPORTS_METRICS_MAP[key as TMetricsMapKey],
      value: key,
      group: getMetricGroup(key as TMetricsMapKey),
    } as TMultiSelectWithChipsOption,
  }),
  {} as Record<TMetricsMapKey, TMultiSelectWithChipsOption>,
)

const dayjs = useDate()

const maxDateRestriction = dayjs().toDate()

const CHART_ID = "cr"

const getMetricType = (metric: TMetricsMapKey) => {
  if (MONETARY_METRICS.includes(metric)) {
    return "currency"
  }
  if (COEF_METRICS.includes(metric)) {
    return "coef"
  }
  if (PERCENTAGE_METRICS.includes(metric)) {
    return "percent"
  }
  return "number"
}

export default function CreativeReportsPage() {
  const {
    isLoading,
    requestReportingItems,
    requestReportingItemsSummary,
    reportingItems,
    reportingItemsSummary,
    depVar,
    totalPages,
    depVarScaler,
    amazonEnabled,
    toggleAmazon,
  } = useConnect()
  const { params, setParams } = useURLSearchParams()
  const { sorting, setSorting } = useUrlSorting(CHART_ID, [
    {
      id: "brand_value_agg",
      desc: true,
    },
  ])
  const [, rerender] = useState(0)

  const creativeReportMode = useMemo<TCreativeReport>(
    () =>
      (params[MODE_URL_KEY] as TCreativeReport) ??
      CREATIVE_REPORT_OPTIONS[0].value,
    [params],
  )

  const selectedMetrics: TMetricsMapKey[] = useMemo(() => {
    const metricsFromParams = params[METRICS_URL_KEY]
      ? (params[METRICS_URL_KEY]?.split(",") as TMetricsMapKey[])
      : []

    return metricsFromParams.length > 0
      ? [...new Set([...ALWAYS_METRICS, ...metricsFromParams])]
      : [...ALWAYS_METRICS, ...DEFAULT_METRICS]
  }, [params])

  const selectedMetricsOptions: TMultiSelectWithChipsOption[] = useMemo(
    () =>
      selectedMetrics.map(id => ({
        ...CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id],
        permanent: ALWAYS_METRICS.includes(id),
      })),
    [selectedMetrics],
  )

  const { dateRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
    false,
    undefined,
    "last30Days",
  )
  const { setPeriod } = usePeriod()

  const { pagination, setPagination } = useUrlPagination(
    CHART_ID,
    ITEMS_PER_PAGE,
  )

  const { columnFilters } = useUrlColumnFilters(CHART_ID)

  useEffect(() => {
    void requestReportingItems(
      creativeReportMode,
      dateRange,
      pagination.pageIndex,
      columnFilters,
      sorting[0],
    )
  }, [
    creativeReportMode,
    dateRange,
    depVar,
    depVarScaler,
    amazonEnabled,
    pagination.pageIndex,
    columnFilters,
    sorting,
  ])

  useEffect(() => {
    void requestReportingItemsSummary(creativeReportMode, dateRange)
  }, [creativeReportMode, dateRange, depVar, depVarScaler, amazonEnabled])

  const setCreativeReportMode = (mode: TCreativeReport) => {
    setParams({
      [MODE_URL_KEY]: mode,
    })
  }

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
  }

  const setSelectedMetrics = (values: TMultiSelectWithChipsOption[]) => {
    const metrics: Array<string | null> = values.map(({ value }) => value)

    setParams({
      [METRICS_URL_KEY]: metrics.join(","),
    })
    rerender(idx => idx + 1)
  }

  const selectedCreativeReportModel = useMemo(
    () =>
      CREATIVE_REPORT_OPTIONS.find(
        option => option.value === creativeReportMode,
      ),
    [creativeReportMode],
  )

  const selectedCreativeReportModeLabel = useMemo(
    () => selectedCreativeReportModel?.label,
    [selectedCreativeReportModel],
  )

  const selectedCreativeReportModePrimaryId = useMemo(
    () => selectedCreativeReportModel?.primaryId,
    [selectedCreativeReportModel],
  )

  const selectedCreativeReportModeSkipTitle = useMemo(
    () => selectedCreativeReportModel?.skipPrimaryId,
    [selectedCreativeReportModel],
  )

  const alwaysOptions = useMemo(
    () => ALWAYS_METRICS.map(id => CREATIVE_REPORTS_METRICS_OPTIONS_MAP[id]),
    [],
  )

  const removableOptions = useMemo(
    () =>
      CREATIVE_REPORTS_METRICS_OPTIONS.filter(
        option => !ALWAYS_METRICS.includes(option.value as TMetricsMapKey),
      ),
    [selectedMetrics],
  )

  const columnConfig = useMemo(
    () =>
      [
        ...(selectedCreativeReportModeSkipTitle &&
        selectedCreativeReportModePrimaryId &&
        selectedCreativeReportModeLabel
          ? []
          : [
              {
                header: selectedCreativeReportModeLabel,
                disableSorting: true,
                key: selectedCreativeReportModePrimaryId,
                render: (row: Record<string, unknown>) => {
                  if (!selectedCreativeReportModePrimaryId) {
                    return ""
                  }
                  const caption = row[selectedCreativeReportModePrimaryId]
                  if (typeof caption !== "string") {
                    return ""
                  }

                  return (
                    <CaptionPreview
                      text={caption}
                      alwaysShow={true}
                      exact={true}
                      fullCaptionClass="max-w-200 word-break break-words"
                      align="left"
                    />
                  )
                },
              },
            ]),
        {
          type: "image",
          header: "Media Preview",
          key: "md_image_url",
          disableSorting: true,
          render: (row: Record<string, unknown>) => {
            const imageUrl = row["md_image_url"]
            // md_image_url is a guessed S3 path that 404s for newer assets;
            // fall back to the platform CDN thumbnail instead of retrying
            // the same broken URL.
            const fallbackUrl = row["thumbnail_url"] ?? row["md_image_url"]

            return (
              <Image
                classes={{
                  image: "w-full h-full",
                  wrapper: "aspect-square overflow-hidden rounded-xl",
                }}
                src={imageUrl as string}
                fallback={fallbackUrl as string}
                alt={row[selectedCreativeReportModePrimaryId ?? ""] as string}
              />
            )
          },
          width: 150,
        },
        ...selectedMetrics.map(metric => ({
          key: metric,
          header: CREATIVE_REPORTS_METRICS_MAP[metric],
          type: getMetricType(metric),
        })),
      ] as TableVisualizationColumn[],
    [
      selectedCreativeReportModeSkipTitle,
      selectedCreativeReportModePrimaryId,
      selectedMetrics,
    ],
  )

  const selectOptions = useMemo(
    () => [
      ...alwaysOptions.map(option => ({
        ...option,
        permanent: true,
      })),
      ...removableOptions,
    ],
    [alwaysOptions, removableOptions],
  )

  const handleSort = (sort: TSortingState) => {
    if (
      Object.prototype.hasOwnProperty.call(sort, "desc") &&
      Object.prototype.hasOwnProperty.call(sort, "id")
    ) {
      setSorting([sort])
    }
  }

  return (
    <WithLoader isLoading={isLoading}>
      <div className="creative-reports-page h-full overflow-y-visible px-system-md">
        <Breadcrumbs
          slug={brandRoutes.CREATIVE_REPORTS.ROOT}
          title={"Creative Reporting"}
          parent={{
            slug: brandRoutes.CREATIVE_REPORTS.ROOT,
            title: "Creative Reports",
          }}
        />
        <div className="mt-6 flex justify-between items-center gap-system-sm">
          <div className="flex items-stretch gap-system-sm flex-shrink-0">
            <DropdownMenu
              options={CREATIVE_REPORT_OPTIONS}
              onItemClick={value =>
                setCreativeReportMode(value as TCreativeReport)
              }
              className="cursor-pointer bg-system-blue-700 border border-system-blue-600 text-base rounded-system-md"
            >
              <div className="flex gap-2 items-center justify-center">
                <ViewfinderCircleIcon className="w-5 h-5 text-white"></ViewfinderCircleIcon>
                <div>Report on: {selectedCreativeReportModeLabel}</div>
                <ChevronDownIcon className="w-system-md h-system-md text-white" />
              </div>
            </DropdownMenu>
          </div>

          <div className="flex items-stretch gap-system-sm flex-shrink-0">
            <TableFiltersBar id={CHART_ID} columnConfig={columnConfig} />
            <SortByControl
              columnConfig={columnConfig}
              sortBy={sorting[0]}
              onSort={handleSort}
            />
            {toggleAmazon && (
              <AmazonToggle
                amazonEnabled={amazonEnabled}
                toggleAmazon={toggleAmazon}
              />
            )}
            {dateRange?.[0] &&
              dateRange?.[1] &&
              isValidDate(dateRange[0]) &&
              isValidDate(dateRange[1]) && (
                <DateRangePicker
                  start={dateRange[0]}
                  end={dateRange[1]}
                  comparisonEnabled={false}
                  maxDate={maxDateRestriction}
                  onChange={changePeriod}
                />
              )}
          </div>
        </div>
        <div className="flex items-stretch w-full mt-system-sm">
          <MultiSelectWithChips
            label="Columns"
            options={selectOptions}
            className="h-system-2xl cursor-pointer"
            groups={METRIC_GROUPS}
            showTypeAhead={true}
            value={selectedMetricsOptions}
            onChange={options => setSelectedMetrics(options)}
          />
        </div>
        {isLoading ? (
          <div className="h-0.5 w-full flex items-center justify-center">
            <Loader size={30} />
          </div>
        ) : (
          <>
            <div className="mt-system-xl grid grid-cols-4 gap-system-sm relative z-10">
              {reportingItems?.map((item, idx) => {
                const itemPrimaryId =
                  item[selectedCreativeReportModePrimaryId ?? ""]
                const imgUrl = item["md_image_url"]
                return (
                  <CreativeReportCard
                    key={`${itemPrimaryId}_${idx}`}
                    imgUrl={imgUrl}
                    primaryIdKey={
                      selectedCreativeReportModePrimaryId ?? "thumbnail_url"
                    }
                    skipTitle={selectedCreativeReportModeSkipTitle}
                    card={item}
                    metricsNames={selectedMetrics}
                  />
                )
              })}
            </div>
            <div className="mt-6">
              <TableVisualization
                metrics={(reportingItems as TMetric[]) ?? []}
                enablePagination={true}
                chartId={CHART_ID}
                paginationState={pagination}
                summaryMetrics={reportingItemsSummary ?? undefined}
                setPaginationState={setPagination}
                columnConfig={columnConfig}
                totalPages={totalPages}
                hideItemsPerPage={true}
              />
            </div>
          </>
        )}
      </div>
    </WithLoader>
  )
}
